import { useState } from 'react';
import { ExportIcon, NotePencilIcon, TrashIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import {
    ActionBar,
    RemoveReportEventConfirmationModal,
    toast,
} from '@/components';
import { ActionType } from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { AppRoutes } from '@/constants/routes';
import { EditReportEventModal } from '@/features/ReportDetail/components/EditReportEventModal/EditReportEventModal';
import { useReportEventClipExport } from '@/hooks/useReportEventClipExport';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { ReportEventDetail } from '@/types/report';
import { exportableReportEvent } from '@/utils/export';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';

interface ReportEventActionBarProps {
    reportId: string;
    reportEvent: ReportEventDetail;
}

export function ReportEventActionBar({
    reportId,
    reportEvent,
}: ReportEventActionBarProps) {
    const { t } = useTranslation([I18nNamespace.REPORTS, I18nNamespace.EVENTS]);
    const navigate = useNavigate();
    const { mutate } = useSWRConfig();
    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [removeInProgress, setRemoveInProgress] = useState(false);
    const clipExport = useReportEventClipExport(reportId);

    const exportable = exportableReportEvent(reportEvent);

    const handleConfirmRemove = async () => {
        if (removeInProgress) {
            return;
        }
        setRemoveInProgress(true);
        try {
            await EngineService.removeReportEvent(reportEvent.id);
            toast.success(t('detail.removeEventSuccess'));
            await Promise.all([
                mutate(
                    (key) =>
                        Array.isArray(key) &&
                        key[0] === CACHE_KEYS.REPORT_EVENTS_PREFIX,
                ),
                revalidateByCacheKeyPrefix(CACHE_KEYS.REPORTS_LIST),
            ]);
            navigate(generatePath(AppRoutes.REPORT, { reportId }), {
                replace: true,
                state: { forceBackToReports: true },
            });
        } catch {
            toast.error(t('detail.removeEventError'));
        } finally {
            setRemoveInProgress(false);
        }
    };

    const actions: ActionConfig[] = [
        {
            type: ActionType.ITEM,
            icon: NotePencilIcon,
            label: t('detail.edit'),
            onClick: () => setIsEditModalOpen(true),
        },
        ...(exportable
            ? [
                  {
                      type: ActionType.ITEM,
                      icon: ExportIcon,
                      label: t('events:actions.export'),
                      disabled: clipExport.busy,
                      onClick: () => void clipExport.startExport(reportEvent),
                  } satisfies ActionConfig,
              ]
            : []),
        {
            type: ActionType.ITEM,
            icon: TrashIcon,
            label: t('detail.remove'),
            onClick: () => setIsRemoveModalOpen(true),
        },
    ];

    return (
        <>
            <ActionBar actions={actions} />
            <EditReportEventModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                reportId={reportId}
                reportEvent={reportEvent}
                onUpdated={() => {}}
            />
            <RemoveReportEventConfirmationModal
                isOpen={isRemoveModalOpen}
                removeInProgress={removeInProgress}
                onConfirm={handleConfirmRemove}
                onCancel={() => setIsRemoveModalOpen(false)}
            />
        </>
    );
}
